import { ActionIcon, Checkbox, createStyles, Loader, useMantineTheme } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch, IconX } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';
import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime';
import { memo, useContext, useEffect, useMemo, useRef, useState, type RefObject } from 'react';

import { useChannelFollowees } from '~/domains/commissioner/hooks/useChannelFollowers';
import { Text } from '~/domains/design-system/Text';
import { TextInput } from '~/domains/design-system/TextInput';
import { Title } from '~/domains/design-system/Title';
import { Button } from '~/domains/design-system/Button';

import { GROUP_SEARCH_QUERY_PARAM } from '../../constants/urlParams';
import { useChannelSearch } from '../../hooks/useChannelSearch';
import { useCurrentUserChannel } from '../../hooks/useCurrentUserChannel';
import { MAX_INVITES } from '../../utils/groupShareMath';

import { GroupUserRow } from './GroupUserRow';
import type { SelectableUser } from './types';

function useInfiniteSentinel({
  rootRef,
  active,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  rootRef: RefObject<HTMLElement>;
  // Sections swap their sentinel <div>; the observer must re-attach when this flips or it watches a detached node.
  active: boolean;
  hasNextPage: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => void;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Gating state is read via ref so a continuously visible sentinel doesn't re-fire on re-observe.
  const stateRef = useRef({ hasNextPage, isFetchingNextPage, fetchNextPage });
  stateRef.current = { hasNextPage, isFetchingNextPage, fetchNextPage };

  useEffect(() => {
    if (!active) return undefined;
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const current = stateRef.current;
        if (entries[0]?.isIntersecting && current.hasNextPage && !current.isFetchingNextPage) {
          current.fetchNextPage();
        }
      },
      { root: rootRef.current ?? null }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // Per-fetch gating comes from stateRef; only the root and section flag re-subscribe.
  }, [rootRef, active]);

  return sentinelRef;
}

type Props = {
  currentUserId?: string;
  /** Selection is the only thing that pins a user above the list. */
  selectedUsers: SelectableUser[];
  onToggleUser: (user: SelectableUser) => void;
  onContinue: () => void;
};
const useStyles = createStyles((theme) => ({
  content: { display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 },
  scrollArea: {
    flex: '0 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: `0 ${theme.other.spacing._24}px`,
  },
  heading: { margin: `${theme.other.spacing._16}px 0 0` },
  subtitle: {
    display: 'block',
    color: theme.colors.gray2[3],
    marginBottom: theme.other.spacing._12,
  },
  sectionTitle: {
    margin: `${theme.other.spacing._16}px 0 ${theme.other.spacing._8}px`,
    color: theme.colors.gray2[4],
  },
  searchInput: {
    marginBottom: theme.other.spacing._16,

    '& .mantine-TextInput-wrapper': {
      borderRadius: theme.radius.xl,
      minHeight: 36,
      height: 36,
      background: theme.colors.gray2[0],
    },
    '& .mantine-TextInput-input': {
      backgroundColor: theme.colors.gray2[0],
      border: 'none',
      borderRadius: theme.radius.xl,
      height: 36,
      minHeight: 36,
    },
  },
  checkbox: {
    '& .mantine-Checkbox-input:checked': {
      backgroundColor: theme.colors.blue[7],
      borderColor: theme.colors.blue[7],
    },
  },
  emptyText: { color: theme.colors.gray2[3], padding: `${theme.other.spacing._8}px 0` },
  limitNotice: {
    display: 'block',
    color: theme.colors.gray2[4],
    padding: `${theme.other.spacing._8}px 0`,
  },
  footer: { padding: theme.other.spacing._16 },
  loaderRow: { display: 'flex', justifyContent: 'center', padding: theme.other.spacing._8 },
}));

function SelectableUserRowComponent({
  user,
  isSelected,
  isDisabled,
  onToggle,
  checkboxClassName,
}: {
  user: SelectableUser;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: (user: SelectableUser) => void;
  checkboxClassName: string;
}) {
  const select = () => {
    if (!isDisabled) onToggle(user);
  };

  return (
    <GroupUserRow
      handle={user.handle}
      avatarUrl={user.avatarUrl}
      onClick={select}
      aria-disabled={isDisabled}
      leftSection={
        <Checkbox
          className={checkboxClassName}
          checked={isSelected}
          disabled={isDisabled}
          onChange={select}
          size="sm"
          data-test-id={isSelected ? 'group-panel-user-checkbox' : undefined}
        />
      }
    />
  );
}

const SelectableUserRow = memo(SelectableUserRowComponent);

export function SelectUsersStep({ currentUserId, selectedUsers, onToggleUser, onContinue }: Props) {
  const { t } = useTranslation('group');
  const { classes } = useStyles();
  const theme = useMantineTheme();
  // useContext, not useRouter: useRouter throws in host tests that mount no router.
  const router = useContext(RouterContext);
  // The URL only mirrors local state - initialize once from it so typing stays synchronous.
  const [searchQuery, setSearchQuery] = useState(() => {
    const queryParam = router?.query[GROUP_SEARCH_QUERY_PARAM];
    if (typeof queryParam === 'string') return queryParam;
    if (Array.isArray(queryParam)) return queryParam[0] ?? '';
    return '';
  });
  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    // Skip the mount render: the URL already holds this value.
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    if (!router) return;
    const nextQuery = { ...router.query };
    if (searchQuery) {
      nextQuery[GROUP_SEARCH_QUERY_PARAM] = searchQuery;
    } else {
      delete nextQuery[GROUP_SEARCH_QUERY_PARAM];
    }
    router.replace({ query: nextQuery }, undefined, { shallow: true });
    // router is excluded: a new object most renders; react only to the query text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);
  const normalizedSearchQuery = searchQuery.trim().replace(/^@/, '');
  const isSearchActive = normalizedSearchQuery.length > 0;
  const [debouncedQuery] = useDebouncedValue(normalizedSearchQuery, 400);
  const { channel } = useCurrentUserChannel(currentUserId);
  const {
    followeesData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage: isFetchingNextFolloweesPage,
  } = useChannelFollowees({
    channelId: channel?.id ?? '',
    enabled: Boolean(channel?.id),
  });
  const {
    results,
    isFetching,
    isFetchingNextPage: isFetchingNextSearchPage,
    fetchNextPage: fetchNextSearchPage,
    hasNextPage: hasNextSearchPage,
  } = useChannelSearch(debouncedQuery, currentUserId);
  const selectedIds = useMemo(
    () => new Set(selectedUsers.map((user) => user.userId)),
    [selectedUsers]
  );
  const searchResults = useMemo(() => {
    const uniqueResults = new Map<string, SelectableUser>();

    results.forEach((user) => {
      // Selected users already render in the pinned block; a match here would show them twice.
      if (!user.userId || user.userId === currentUserId || selectedIds.has(user.userId)) return;
      uniqueResults.set(user.userId, {
        userId: user.userId,
        handle: user.handle ?? '',
        name: user.name ?? user.handle ?? '',
        avatarUrl: user.avatarUrl,
      });
    });

    return Array.from(uniqueResults.values());
  }, [currentUserId, results, selectedIds]);
  const followees = useMemo(
    () =>
      (followeesData ?? [])
        .map((user) => ({
          userId: user.userId,
          handle: user.handle,
          name: user.name,
          avatarUrl: user.avatarUrl,
        }))
        .filter((user) => !selectedIds.has(user.userId) && user.userId !== currentUserId),
    [currentUserId, followeesData, selectedIds]
  );
  const maxed = selectedUsers.length >= MAX_INVITES;
  const renderRow = (user: SelectableUser) => (
    <SelectableUserRow
      key={user.userId}
      user={user}
      isSelected={selectedIds.has(user.userId)}
      isDisabled={!selectedIds.has(user.userId) && maxed}
      onToggle={onToggleUser}
      checkboxClassName={classes.checkbox}
    />
  );
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const searchSentinelRef = useInfiniteSentinel({
    rootRef: scrollAreaRef,
    active: isSearchActive,
    hasNextPage: hasNextSearchPage,
    isFetchingNextPage: isFetchingNextSearchPage,
    fetchNextPage: () => fetchNextSearchPage(),
  });
  const followeesSentinelRef = useInfiniteSentinel({
    rootRef: scrollAreaRef,
    active: !isSearchActive,
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage: isFetchingNextFolloweesPage,
    fetchNextPage: () => fetchNextPage(),
  });
  return (
    <div className={classes.content}>
      <div className={classes.scrollArea} ref={scrollAreaRef}>
        <Title variant="title-x-large" className={classes.heading} tag="h3">
          {t('panel.groupWith')}
        </Title>
        <Text variant="title-medium" className={classes.subtitle} component="p">
          {t('panel.groupWithSubtitle')}
        </Text>
        <TextInput
          variant="filled"
          className={classes.searchInput}
          placeholder={t('panel.searchPlaceholder')}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          autoComplete="off"
          icon={<IconSearch size={20} color={theme.colors.gray2[3]} />}
          rightSection={
            searchQuery ? (
              <ActionIcon size="sm" variant="subtle" onClick={() => setSearchQuery('')}>
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
        />
        {/* Sits above the rows it explains: without it the disabled checkboxes below read as broken. */}
        {maxed && (
          <Text variant="body-medium" className={classes.limitNotice} component="p" role="status">
            {t('panel.inviteLimitReached', { max: MAX_INVITES })}
          </Text>
        )}
        {selectedUsers.map((user) => renderRow(user))}
        {isSearchActive && (
          <>
            {/* Fresh-query loader only: a next-page fetch also flips isFetching and must not blank loaded rows. */}
            {isFetching && searchResults.length === 0 && (
              <div className={classes.loaderRow}>
                <Loader size="sm" />
              </div>
            )}
            {/* "No results" is gated on the settled query so it never speaks for a stale query. */}
            {normalizedSearchQuery === debouncedQuery &&
              !isFetching &&
              searchResults.length === 0 && (
                <Text variant="body-medium" className={classes.emptyText} component="p">
                  {t('panel.noSearchResult', { query: searchQuery.trim() })}
                </Text>
              )}
            {/* The rows are not: settled results stay put while a retyped query debounces. */}
            {searchResults.map((user) => renderRow(user))}
            <div ref={searchSentinelRef} />
            {isFetchingNextSearchPage && (
              <div className={classes.loaderRow}>
                <Loader size="sm" />
              </div>
            )}
          </>
        )}
        {!isSearchActive && (
          <>
            <Text variant="subhead-large" className={classes.sectionTitle} component="h4">
              {t('panel.peopleYouFollow')}
            </Text>
            {isLoading && (
              <div className={classes.loaderRow}>
                <Loader size="sm" />
              </div>
            )}
            {!isLoading && followees.length === 0 && (
              <Text variant="body-medium" className={classes.emptyText} component="p">
                {t('panel.noFollowees')}
              </Text>
            )}
            {!isLoading && followees.map((user) => renderRow(user))}
            <div ref={followeesSentinelRef} />
            {isFetchingNextFolloweesPage && (
              <div className={classes.loaderRow}>
                <Loader size="sm" />
              </div>
            )}
          </>
        )}
      </div>
      {selectedUsers.length > 0 && (
        <div className={classes.footer}>
          <Button fullWidth variant="primary-fill" size="large" onClick={onContinue}>
            {t('panel.continue')}
          </Button>
        </div>
      )}
    </div>
  );
}
